// src/components/SaverCreditRateChart.jsx
import React, { useMemo, useState } from "react";
import "./SaverCreditRateChart.css";

// Filing statuses
const FILING_STATUSES = [
  { key: "married_joint", label: "Married filing jointly" },
  { key: "head", label: "Head of household" },
  { key: "single", label: "All other filers" },
];

// ✅ 10k increments ending in 5
const AGI_ROWS = [
  25000,
  35000,
  45000,
  55000,
  65000,
  75000,
].map((v) => ({
  value: v,
  label: `$${(v / 1000).toFixed(0)}k`,
}));

// ✅ EXACT Saver’s Credit thresholds — Tax Year 2025
function getRate(status, agi) {
  if (!status || !Number.isFinite(agi)) return null;

  if (status === "married_joint") {
    if (agi <= 47500) return "50%";
    if (agi <= 51000) return "20%";
    if (agi <= 79000) return "10%";
    return "0%";
  }

  if (status === "head") {
    if (agi <= 35625) return "50%";
    if (agi <= 38250) return "20%";
    if (agi <= 59250) return "10%";
    return "0%";
  }

  if (status === "single") {
    if (agi <= 23750) return "50%";
    if (agi <= 25500) return "20%";
    if (agi <= 39500) return "10%";
    return "0%";
  }

  return null;
}

export default function SaverCreditRateChart() {
  const [activeStatus, setActiveStatus] = useState(null);
  const [activeAgi, setActiveAgi] = useState(null);

  const exampleText = useMemo(() => {
    if (!activeStatus || !activeAgi) return null;
    const statusLabel =
      FILING_STATUSES.find((s) => s.key === activeStatus)?.label || "";
    const rate = getRate(activeStatus, activeAgi);
    return `${statusLabel}, AGI $${activeAgi.toLocaleString()} → ${rate}`;
  }, [activeStatus, activeAgi]);

  return (
    <section className="sc-chart">
      <div className="sc-chart__head">
        <div className="sc-chart__title">
<strong>
  <a
    href="https://www.irs.gov/pub/irs-pdf/f8880.pdf"
    target="_blank"
    rel="noopener noreferrer"
    style={{ textDecoration: "underline" }}
  >
    Form 8880
  </a>{" "}
  Saver’s Credit
</strong>

        </div>
        <div className="sc-chart__sub">
         Learn how to use the table. Try Single (All other filers) and $35k.

        </div>
        {exampleText && (
          <div className="sc-chart__sub">
            <strong>{exampleText}</strong>
          </div>
        )}
      </div>

      <div className="sc-grid-wrapper">
        <table className="sc-grid">
          <thead>
            <tr>
              <th className="sc-empty-cell">AGI ↓ / Filing →</th>
              {FILING_STATUSES.map((s) => {
                const isActive = activeStatus === s.key;
                return (
                  <th
                    key={s.key}
                    className={`sc-col-header ${isActive ? "active-col" : ""}`}
                    onClick={() => setActiveStatus(s.key)}
                  >
                    {s.label}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {AGI_ROWS.map((row) => {
              const isActiveRow = activeAgi === row.value;

              return (
                <tr key={row.value}>
                  <th
                    className={`sc-row-header ${
                      isActiveRow ? "active-row" : ""
                    }`}
                    onClick={() => setActiveAgi(row.value)}
                  >
                    {row.label}
                  </th>

                  {FILING_STATUSES.map((status) => {
                    const isColActive = activeStatus === status.key;
                    const rate = getRate(status.key, row.value);
                    const isIntersection = isActiveRow && isColActive;

                    let stateClass = "neutral";
                    if (rate === "0%") stateClass = "no-credit";
                    if (rate && rate !== "0%") stateClass = "has-credit";

                    return (
                      <td
                        key={status.key}
                        className={`sc-cell
                          ${stateClass}
                          ${isActiveRow ? "row-lit" : ""}
                          ${isColActive ? "col-lit" : ""}
                          ${isIntersection ? "intersection" : ""}
                        `}
                      >
                        {rate}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="sc-chart__note">
        AGI rows are examples. Actual rates are determined using IRS Form 8880
        thresholds.
      </div>
    </section>
  );
}
